import {
  addLicensesForPeriod,
  calculateTaxFailure,
  calculateTaxStarted,
  calculateTaxSucceeded,
  loadLicensesForPeriodFailure,
  loadLicensesForPeriodStarted,
  taxPaymentPeriodFailure,
  taxPaymentPeriodsStarted,
  taxPaymentPeriodSucceeded,
} from './taxSlice';
import api from '../../../api';
import { AppThunk } from 'src/store/store';
import { deriveConfigExpressTaxCertifications } from '../config/configSelectors';
import { setPaymentID, setPaymentFees } from '../payment/paymentSlice';
import { TaxConfigurationInformation } from 'src/types/configuration';
import {
  CalculatedTot,
  ExtendedPaymentPeriod,
  PaymentPeriod,
  PeriodLicense,
} from 'src/types/tax';
import { PaymentFees } from 'src/types/payment';
import { C_PERMIT_STATUS_CURRENT } from 'src/common/consts';
import { roundDecimalPlaces } from 'src/common/utils';
import * as Sentry from '@sentry/react';
import { License } from 'src/types/licenses';
import dayjs from 'dayjs';

const { logger } = Sentry;

function isLicenseInactive(
  licensesList: Array<License>,
  license: PeriodLicense
) {
  if (licensesList) {
    const matchedLicense = licensesList.find(
      (lic) => lic.license === license?.license
    );

    if (
      matchedLicense?.status !== C_PERMIT_STATUS_CURRENT &&
      !license?.isActionable
    ) {
      return true;
    }
  }
  return false;
}

export const getTaxPaymentPeriodsAsync = (): AppThunk<void, typeof api> => {
  return async (dispatch, getState, api) => {
    dispatch(taxPaymentPeriodsStarted());
    let error;
    try {
      const periods = await api.getTaxablePeriods(getState());
      const licenses = await api.getAllLicensesForUserAsync(
        getState().auth?.idToken
      );

      const taxableLicenses = await api.getTaxableLicenses(
        [periods[0].startDate],
        getState()
      );

      const paymentPeriods = periods.map(
        (period, index): ExtendedPaymentPeriod => {
          const startDate = dayjs(period.startDate);
          const endDate = dayjs(period.endDate);
          return {
            ...period,
            id: startDate.unix().toString(),
            year: endDate.year(),
            selected: index === 0,
            licenses: (taxableLicenses[period.startDate] || [])
              .filter(Boolean)
              .map((lic) => ({
                ...lic,
                isInactive: isLicenseInactive(licenses, lic),
                selected: false,
              })),
          };
        }
      );

      dispatch(
        taxPaymentPeriodSucceeded({
          paymentPeriods,
          licensesPerPeriod: {
            [paymentPeriods[0].startDate]: paymentPeriods[0].licenses,
          },
        })
      );
    } catch {
      error = 'Something went wrong while fetching payment periods.';
    } finally {
      if (error) {
        dispatch(taxPaymentPeriodFailure(error));
      }
    }
  };
};

export const loadLicensesForPeriod = (
  period: PaymentPeriod
): AppThunk<void, typeof api> => {
  return async (dispatch, getState, api) => {
    let error;
    dispatch(loadLicensesForPeriodStarted(period.startDate));
    try {
      const licenses = await api.getTaxableLicenses(
        [period.startDate],
        getState()
      );
      dispatch(
        addLicensesForPeriod({ licenses: licenses[period.startDate], period })
      );
    } catch {
      error = 'Something went wrong while fetching licenses for period.';
    } finally {
      if (error) {
        dispatch(loadLicensesForPeriodFailure(error));
      }
    }
  };
};
const getTaxableActivitiesFromBuckets = (
  license: PeriodLicense,
  dateStart: string,
  dateEnd: string
) => {
  return license.buckets?.map((bucket) => {
    const totalRevenue = bucket.revenue ?? 0;
    const allowableDeductions = bucket.discounts ? Number(bucket.discounts) : 0;
    const taxableReceipts = roundDecimalPlaces(
      Number(totalRevenue) - Number(allowableDeductions)
    );
    return {
      dateStart,
      dateEnd,
      taxableReceipts,
      totalRevenue,
      allowableDeductions,
      numDaysOccupied: license.occupancy?.occupied || 0,
      numDaysAvailable: license.occupancy?.available || 0,
      provider: bucket.provider,
    };
  });
};

const transformTaxableReceiptsForCalculateMultiPropertyTot = (
  paymentPeriods: Array<PaymentPeriod>
) => {
  return paymentPeriods.flatMap((paymentPeriod) => {
    return paymentPeriod.licenses.map((license) => {
      const taxableActivities = getTaxableActivitiesFromBuckets(
        license,
        paymentPeriod.startDate,
        paymentPeriod.endDate
      );

      return {
        license: license.license,
        dateStart: paymentPeriod.startDate,
        dateEnd: paymentPeriod.endDate,
        misc: {
          taxableActivities,
        },
      };
    });
  });
};

const calculateBreakdownValue = (data: CalculatedTot, key: string) => {
  let totalDue = 0;
  data.details.forEach((detail) => {
    if (detail.misc && detail.misc.taxableActivities) {
      detail.misc.taxableActivities.forEach((activity) => {
        if (activity.taxTypes) {
          Object.values(activity.taxTypes).forEach((taxType) => {
            totalDue += taxType[key] || 0;
          });
        }
      });
    }
  });
  return totalDue;
};

const calculateBreakdownValueForTaxType = (
  data: CalculatedTot,
  taxTypeKey: string,
  key: string
) => {
  let totalDue = 0;
  data.details.forEach((detail) => {
    if (detail.misc && detail.misc.taxableActivities) {
      detail.misc.taxableActivities.forEach((activity) => {
        if (activity.taxTypes && activity.taxTypes[taxTypeKey]) {
          totalDue += activity.taxTypes[taxTypeKey][key] || 0;
        }
      });
    }
  });
  return totalDue;
};

const configTaxTypeKeyExistsInData = (
  data: CalculatedTot,
  configKey: string
): boolean => {
  return data.details.some((detail) =>
    detail.misc.taxableActivities.some(
      (activity) => activity.taxTypes[configKey]
    )
  );
};

const transformDataForPaymentFees = (
  data: CalculatedTot,
  taxConfig: Array<TaxConfigurationInformation>
) => {
  // save payment data in redux
  // get total for all breakdown items in the tax config
  const breakdown: Array<{ key: string; label: string; value: number }> = [];

  // each tax type is treated individually
  taxConfig.forEach((config) => {
    // only include tax type if it is in the config and there is a matching item in the data
    if (configTaxTypeKeyExistsInData(data, config.key)) {
      config.breakdown.forEach((item) => {
        if (item.key === 'totDue') {
          const value = calculateBreakdownValueForTaxType(
            data,
            config.key,
            item.key
          );
          breakdown.push({
            key: `${config.key}_${item.key}`,
            label: config.label,
            value,
          });
        }
      });
    }
  });

  // fees are summed across tax types
  taxConfig.forEach((config) => {
    // only include tax type if it is in the config and there is a matching item in the data
    if (configTaxTypeKeyExistsInData(data, config.key)) {
      config.breakdown.forEach((item) => {
        if (
          !breakdown.some((breakdownItem) => breakdownItem.key === item.key)
        ) {
          if (item.key !== 'totDue') {
            const sum = calculateBreakdownValue(data, item.key);
            breakdown.push({ key: item.key, label: item.label, value: sum });
          }
        }
      });
    }
  });

  const paymentFees: PaymentFees = {
    ccFee: data?.ccFee,
    ccTotal: data?.ccTotal,
    achFee: data?.achFee,
    achTotal: data?.achTotal,
    checkFee: data?.checkFee,
    checkTotal: data?.checkTotal,
    breakdown,
    summary: data?.summary,
  };

  return paymentFees;
};

const prepareMandatoryAcknowledgements = (taxCertifications: string[] = []) =>
  taxCertifications.reduce(
    (acknowledgements, certification, index) => ({
      ...acknowledgements,
      [`aff_${index + 1}`]: { label: certification },
    }),
    {}
  );

export const calculateMultiPropertyTaxAsync = (
  paymentPeriods: Array<PaymentPeriod>,
  taxConfig: Array<TaxConfigurationInformation>
): AppThunk<Promise<boolean>, typeof api> => {
  return async (dispatch, getState, api) => {
    dispatch(calculateTaxStarted());

    const CHUNK_SIZE = 25;

    const convertedPaymentPeriods =
      transformTaxableReceiptsForCalculateMultiPropertyTot(paymentPeriods);

    const mandatoryAcknowldgements =
      deriveConfigExpressTaxCertifications(getState());
    const preparedMandatoryAcknowldgement = prepareMandatoryAcknowledgements(
      mandatoryAcknowldgements
    );

    try {
      let data: CalculatedTot = { details: [] };
      let paymentId = null;

      for (
        let chunkStartIdx = 0;
        chunkStartIdx < convertedPaymentPeriods.length;
        chunkStartIdx += CHUNK_SIZE
      ) {
        const chunkEndIdx = Math.min(
          convertedPaymentPeriods.length,
          chunkStartIdx + CHUNK_SIZE
        );

        const paymentPeriodsInChunk = convertedPaymentPeriods.slice(
          chunkStartIdx,
          chunkEndIdx
        );

        const requestBody = {
          paymentPeriods: paymentPeriodsInChunk,
          numPaymentPeriods: convertedPaymentPeriods.length,
          paymentId,
          mandatoryAcknowldgements: preparedMandatoryAcknowldgement,
        };

        logger.info('Endpoint Call /calculateMultiPropertyTot:', {
          place: getState().config?.settings?.place,
          paymentPeriods: paymentPeriodsInChunk.map((period) => ({
            startDate: period.dateStart,
            endDate: period.dateEnd,
            license: period.license,
            providers:
              period.misc.taxableActivities?.map((activity) => ({
                provider: activity.provider,
                taxableReceipts: activity.taxableReceipts,
                numDaysAvailable: activity.numDaysAvailable,
                numDaysOccupied: activity.numDaysOccupied,
              })) || [],
          })),
        });

        const result = await api.calculateMultiPropertyTotAsync(
          getState(),
          requestBody
        );
        data = result.data;
        paymentId = data.paymentId;

        logger.info('Endpoint Response /calculateMultiPropertyTot:', {
          place: getState().config?.settings?.place,
          paymentId,
          details: data.details,
        });
      }
      if (data) {
        // save payment data in redux
        const paymentFees = transformDataForPaymentFees(data, taxConfig);

        // save raw response in redux
        dispatch(calculateTaxSucceeded(data));

        // update paymentID in redux
        dispatch(setPaymentID(data.paymentId));

        dispatch(setPaymentFees(paymentFees));
      }
      return true;
    } catch (error) {
      dispatch(
        calculateTaxFailure('Something went wrong while calculating tax.')
      );
      return false;
    }
  };
};
